import { useEffect, useState, useCallback } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import type { JiraProject, JiraProjects } from '../__types__/apiUtils';
import type { IssueType } from '../providers/BulkCreateContextProvider';

import { useGetAvailableSitesForSingleIssueCreateForm } from './useGetAvailableSitesForSingleIssueCreateForm';
import { useGetJiraProjectsForSingleIssueCreateForm } from './useGetJiraProjectsForSingleIssueCreateForm';
import { useGetIssueTypesForSingleIssueCreateForm } from './useGetIssueTypesForSingleIssueCreateForm';

type SitePickerData = {
	availableSites: AvailableSite[];
	selectedSite: AvailableSite | null;
	isSitePickerLoading: boolean;
	error: Error | null;
};

type ProjectPickerData = {
	selectedProject: JiraProject | null;
	optionsRecentProjects: JiraProjects;
	optionsAllProjects: JiraProjects;
	isProjectPickerLoading: boolean;
	error: Error | null;
};

type IssueTypePickerData = {
	selectedIssueType: IssueType | null;
	optionsIssueTypes: IssueType[];
	isIssueTypePickerLoading: boolean;
	error: Error | null;
};

export type SingleIssueCreateInitialData = SitePickerData &
	ProjectPickerData &
	IssueTypePickerData & {
		error: Error | null;
		onSiteChange: (site: AvailableSite | null) => void;
		onProjectChange: (project: JiraProject | null) => void;
		onIssueTypeChange: (issueType: IssueType | null) => void;
	};

export const useGetInitialDataForSingleIssueCreateForm = (): SingleIssueCreateInitialData => {
	// Local state for site and project
	const [selectedSite, setSelectedSite] = useState<AvailableSite | null>(null);
	const [selectedProject, setSelectedProject] = useState<JiraProject | null>(null);
	const [projectSelectorQuery, setProjectSelectorQuery] = useState<string | null>(null);
	const [managedSelectedIssueType, setManagedSelectedIssueType] = useState<IssueType | null>(null);

	// Site picker
	const {
		availableSites = [],
		selectedAvailableSite,
		isLoading: isSitePickerLoading = false,
		error: sitesError = null,
	} = useGetAvailableSitesForSingleIssueCreateForm();

	useEffect(() => {
		if (selectedAvailableSite) {
			setSelectedSite(selectedAvailableSite);
		}
	}, [selectedAvailableSite]);

	// Project picker
	const {
		optionsRecentProjects,
		optionsAllProjects,
		selectedProject: projectFromHook,
		isLoading: isProjectPickerLoading,
		error: projectsError,
		onProjectChange: handleProjectChangeFromChildHook,
	} = useGetJiraProjectsForSingleIssueCreateForm(selectedSite, projectSelectorQuery);

	useEffect(() => {
		if (projectFromHook) {
			setSelectedProject(projectFromHook);
		}
	}, [projectFromHook]);

	// Issue type picker (child hook)
	const {
		optionsIssueTypes,
		selectedIssueType: issueTypeFromChildHook,
		isLoading: isIssueTypePickerLoading,
		error: issueTypesError,
	} = useGetIssueTypesForSingleIssueCreateForm(selectedSite, selectedProject);

	// Sync managedSelectedIssueType with the default/auto-selected one from child hook
	useEffect(() => {
		setManagedSelectedIssueType(issueTypeFromChildHook);
	}, [issueTypeFromChildHook]);

	// Handler for site changes by the user
	const handleSiteChange = useCallback(
		(site: AvailableSite | null) => {
			setSelectedSite(site);
			setSelectedProject(null); // Reset project
			setManagedSelectedIssueType(null); // Reset issue type
			setProjectSelectorQuery(null);
		},
		[setSelectedSite, setSelectedProject, setManagedSelectedIssueType, setProjectSelectorQuery],
	);

	// Handler for project changes by the user
	const handleProjectChange = useCallback(
		(project: JiraProject | null) => {
			setSelectedProject(project);
			setManagedSelectedIssueType(null);
			if (handleProjectChangeFromChildHook) {
				handleProjectChangeFromChildHook(project);
			}
		},
		[setSelectedProject, setManagedSelectedIssueType, handleProjectChangeFromChildHook],
	);

	// Handler for issue type changes by the user
	const handleIssueTypeChange = useCallback(
		(issueType: IssueType | null) => {
			setManagedSelectedIssueType(issueType);
		},
		[setManagedSelectedIssueType],
	);

	const singleIssueCreateInitialData: SingleIssueCreateInitialData = {
		availableSites,
		selectedSite,
		isSitePickerLoading,
		selectedProject,
		optionsRecentProjects,
		optionsAllProjects,
		isProjectPickerLoading,
		optionsIssueTypes,
		selectedIssueType: managedSelectedIssueType,
		isIssueTypePickerLoading,
		error: sitesError || projectsError || issueTypesError,
		onSiteChange: handleSiteChange,
		onProjectChange: handleProjectChange,
		onIssueTypeChange: handleIssueTypeChange,
	};

	return singleIssueCreateInitialData;
};
